import React, { useState, useEffect } from 'react';
import { topicApi } from '../services/api.ts';
import { authService } from '../services/auth.ts';
import TruncatedDescription from './TruncatedDescription.tsx';
import PresentersList from './PresentersList.tsx';
import '../App.css'; // Import existing app styles

// Add custom styles for this component
const feedbackPageStyles = `
  .feedback-page .star-rating {
    justify-content: flex-start !important;
  }
  .feedback-page .modal {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif !important;
  }
  .feedback-page .modal h3,
  .feedback-page .modal label,
  .feedback-page .modal p,
  .feedback-page .modal button,
  .feedback-page .modal input {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif !important;
  }
`;

interface Topic {
  topicId: string;
  title: string;
  description: string;
  submittedBy: string;
  presenters?: string[];
  teamId: string;
  sessionType: string;
  presentedAt?: string;
}

interface Team {
  teamId: string;
  teamName: string;
}

interface FeedbackPageProps {
  topicId: string;
  teamId: string;
  onComplete: () => void;
}

const FeedbackPage: React.FC<FeedbackPageProps> = ({ topicId, teamId, onComplete }) => {
  const [topic, setTopic] = useState<Topic | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  // Form state
  const [usefulnessRating, setUsefulnessRating] = useState<number>(0);
  const [recommendationRating, setRecommendationRating] = useState<number>(0);
  const [customerConversations, setCustomerConversations] = useState<string>('');
  const [hoursSaved, setHoursSaved] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasExistingRating, setHasExistingRating] = useState(false);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Not presented yet';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    const loadTopicAndTeam = async () => {
      try {
        setLoading(true);
        
        // Load topic details
        const topicResponse = await topicApi.getTopicById(topicId);
        if (!topicResponse) {
          throw new Error('Topic not found');
        }
        setTopic(topicResponse);

        // Load team details
        const teams = await topicApi.getTeams();
        const targetTeam = teams.find(t => t.teamId === teamId);
        if (!targetTeam) {
          throw new Error('Team not found');
        }
        setTeam(targetTeam);

        // Load existing rating if user has already provided feedback
        const userInfo = authService.getUserInfo();
        const userAlias = userInfo?.username || '';
        
        if (userAlias) {
          try {
            const ratings = await topicApi.getRatings(topicId);
            const userRating = ratings.find((rating: any) => rating.userId === userAlias);
            
            if (userRating) {
              setUsefulnessRating(userRating.usefulnessRating || 0);
              setRecommendationRating(userRating.recommendationRating || 0);
              setCustomerConversations(userRating.customerConversations?.toString() || '');
              setHoursSaved(userRating.hoursSaved?.toString() || '');
              setHasExistingRating(true);
            }
          } catch (error) {
            console.error('Failed to load existing rating:', error);
          }
        }

      } catch (error) {
        console.error('Error loading topic/team:', error);
        setLoadError('Topic or team not found. The link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };

    loadTopicAndTeam();
  }, [topicId, teamId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const userInfo = authService.getUserInfo();
    const userAlias = userInfo?.username || '';

    if (!userAlias) {
      setError('User not authenticated');
      return;
    }

    if (usefulnessRating === 0 && recommendationRating === 0 && 
        customerConversations.trim() === '' && hoursSaved.trim() === '') {
      setError('Please provide at least one rating, conversation count, or hours saved');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const conversationsCount = customerConversations.trim() ? parseInt(customerConversations.trim()) : undefined;
      const hoursCount = hoursSaved.trim() ? parseFloat(hoursSaved.trim()) : undefined;
      
      const rating = {
        userAlias: userAlias,
        usefulnessRating: usefulnessRating > 0 ? usefulnessRating : undefined,
        recommendationRating: recommendationRating > 0 ? recommendationRating : undefined,
        customerConversations: conversationsCount,
        hoursSaved: hoursCount
      };

      await topicApi.submitRating(topicId, rating);
      setError('✅ Feedback submitted successfully! Redirecting...');
      
      // Success - redirect back to main app
      setTimeout(() => onComplete(), 1500);
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRating = async () => {
    if (!window.confirm('Are you sure you want to delete your feedback for this topic? This action cannot be undone.')) {
      return;
    }

    const userInfo = authService.getUserInfo();
    const userAlias = userInfo?.username || '';

    if (!userAlias) {
      setError('User not authenticated');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await topicApi.deleteRating(topicId, userAlias);
      setError('✅ Feedback deleted successfully! Redirecting...');
      
      // Success - redirect back to main app
      setTimeout(() => onComplete(), 1500);
    } catch (error: any) {
      console.error('Error deleting feedback:', error);
      setError('Failed to delete feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading topic details...</p>
        </div>
      </div>
    );
  }

  if (loadError || !topic || !team) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Topic Not Found</h1>
          <p className="text-gray-600 mb-4">{loadError}</p>
          <button
            onClick={onComplete}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Return to App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-page min-h-screen bg-gray-50 py-8">
      <style>{feedbackPageStyles}</style>
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md">
        {/* Modal Content */}
        <div className="modal" style={{ padding: '20px' }}>
          <h3>Feedback</h3>
          <p style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', fontSize: '14px', color: '#666', margin: '0 0 15px 0' }}>
            Providing feedback as <span style={{ color: '#2563eb', fontWeight: '600' }}>{authService.getUserInfo()?.name || 'Unknown User'}</span>
          </p>
          {(usefulnessRating > 0 || recommendationRating > 0 || customerConversations.trim() !== '' || hoursSaved.trim() !== '') && (
            <p style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', fontSize: '14px', color: '#666', margin: '0 0 15px 0' }}>
              (you already provided feedback but feel free to modify it!)
            </p>
          )}
          
          {/* Topic Details - matching modal format exactly */}
          <div className="topic-details">
            <p>Topic: <strong>{topic.title}</strong></p>
            <p>Description: <strong><TruncatedDescription description={topic.description || 'No description provided'} topicId={topic.topicId} /></strong></p>
            <p>Presenter(s): <strong>
              <PresentersList 
                presenters={topic.presenters || []} 
                currentUser={authService.getUserInfo()?.username}
                displayMode="inline"
              />
            </strong></p>
            <p>Presented to: <strong>{team.teamName}</strong></p>
            <p>Date Presented: <strong>{formatDate(topic.presentedAt)}</strong></p>
          </div>

          {/* Feedback Form - using exact modal structure */}
          <form onSubmit={handleSubmit}>
            {/* Confidence Impact Rating */}
            <div className="rating-group">
              <label>How much did this topic boost your confidence or knowledge? (optional):</label>
              <div className="star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setUsefulnessRating(star)}
                    className={`star ${star <= usefulnessRating ? 'filled' : ''}`}
                  >
                    ★
                  </button>
                ))}
                {usefulnessRating > 0 && (
                  <>
                    <span className="rating-text">({usefulnessRating}/5)</span>
                    <button
                      type="button"
                      className="clear-rating"
                      onClick={() => setUsefulnessRating(0)}
                    >
                      Clear
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Recommendation Rating */}
            <div className="rating-group">
              <label>How likely are you to recommend a colleague watch a recording or read the summary of this topic? (optional):</label>
              <div className="star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRecommendationRating(star)}
                    className={`star ${star <= recommendationRating ? 'filled' : ''}`}
                  >
                    ★
                  </button>
                ))}
                {recommendationRating > 0 && (
                  <>
                    <span className="rating-text">({recommendationRating}/5)</span>
                    <button
                      type="button"
                      className="clear-rating"
                      onClick={() => setRecommendationRating(0)}
                    >
                      Clear
                    </button>
                  </>
                )}
              </div>
            </div>

            <div style={{ borderTop: '1px solid #e5e7eb', margin: '20px 0 15px 0' }}></div>

            <p style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif', fontSize: '14px', color: '#666', margin: '0 0 10px 0', fontWeight: '500' }}>
              You can modify or add this feedback later:
            </p>

            {/* Hours Saved */}
            <div className="form-group">
              <label>Roughly how many hours of research or preparation did this topic save you? (optional):</label>
              <input
                type="number"
                step="0.25"
                value={hoursSaved}
                onChange={(e) => setHoursSaved(e.target.value)}
                placeholder="Enter in hours, ie: 2.5 or 0.5"
                min="0"
                className="hours-input"
              />
            </div>

            {/* Customer Conversations */}
            <div className="form-group">
              <label>How many customers did this topic help you speak with? (optional):</label>
              <input
                type="number"
                value={customerConversations}
                onChange={(e) => setCustomerConversations(e.target.value)}
                placeholder="Enter number of customers"
                min="0"
                className="customers-input"
              />
            </div>

            {/* Error/Success Message */}
            {error && <div className="error-message">{error}</div>}

            {/* Submit Button */}
            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                {hasExistingRating && (
                  <button 
                    type="button" 
                    onClick={handleDeleteRating}
                    disabled={isSubmitting}
                    style={{ 
                      backgroundColor: '#dc2626', 
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Delete My Feedback
                  </button>
                )}
              </div>
              <div>
                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
                <button type="button" onClick={onComplete}>
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;
